import React, { useEffect, useState } from "react";
import axiosInstance from "../axios/axios";
import appliedIcon from "../assets/appliedIcon.svg";
import interviewIcon from "../assets/interiewIcon.svg";
import rejectIcon from "../assets/rejectIcon.svg";
import Loader from "./Loader";
import viewIcon from "../assets/viewIcon.svg";

const StudentApplications = () => {
  const filters = ["Applied", "Viewed", "Interview", "Rejected"];
  const [activeFilter, setActiveFilter] = useState(filters[0]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const user_id = localStorage.getItem("user_id");

  useEffect(() => {
    if (!user_id) {
      setError("Please log in.");
      setLoading(false);
      return;
    }

    const fetchApplications = async (user_id) => {
      try {
        const response = await axiosInstance.get(
          `user-applications-job/${user_id}`
        );
        console.log(response.data.applications.data);
        setApplications(response.data.applications.data || []);
        setError(null);
      } catch (err) {
        console.error("Error fetching applications:", err);
        setError("Failed to load applications. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchApplications(user_id);
  }, [user_id]);

  const filteredItems = applications.filter((item) => {
    if (activeFilter === "Rejected") {
      return item.status === "Open";
    }
    if (activeFilter === "Applied") {
      return item.status === "Applied" || item.status === "Accepted";
    }
    return item.status === activeFilter;
  });

  const truncateText = (text, maxLength) => {
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  // Get status badge configuration
  const getStatusConfig = (status) => {
    const configs = {
      Applied: {
        icon: appliedIcon,
        text: "Applied",
        className: "bg-blue-100 text-blue-700 border-blue-200"
      },
      Accepted: {
        icon: appliedIcon,
        text: "Accepted",
        className: "bg-green-100 text-green-700 border-green-200"
      },
      Viewed: {
        icon: viewIcon,
        text: "Viewed",
        className: "bg-yellow-100 text-yellow-700 border-yellow-200"
      },
      Interview: {
        icon: interviewIcon,
        text: "Interview Scheduled",
        className: "bg-orange-100 text-orange-700 border-orange-200"
      },
      Rejected: {
        icon: rejectIcon,
        text: "Rejected",
        className: "bg-red-100 text-red-700 border-red-200"
      }
    };
    return configs[status] || configs.Applied;
  };

  const getFilterCount = (filter) => {
    return applications.filter((item) => {
      if (filter === "Rejected") {
        return item.status === "Open";
      }
      if (filter === "Applied") {
        return item.status === "Applied" || item.status === "Accepted";
      }
      return item.status === filter;
    }).length;
  };

  if (loading) return <Loader />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 sm:px-6 py-4">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
            My Applications
          </h1>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-6">
        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-800 text-sm font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Filter Tabs - Horizontal scroll for all screen sizes */}
        <div className="mb-6">
          <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
            {filters.map((filter) => (
              <button
                key={filter}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-200 flex-shrink-0 min-w-fit ${
                  activeFilter === filter
                    ? "bg-green-600 text-white shadow-sm"
                    : "bg-gray-100 text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                }`}
                onClick={() => setActiveFilter(filter)}
              >
                <span>{filter}</span>
                <span className={`inline-flex items-center justify-center w-5 h-5 text-xs rounded-full ${
                  activeFilter === filter 
                    ? "bg-green-500 text-white" 
                    : "bg-gray-300 text-gray-600"
                }`}>
                  {getFilterCount(filter)}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Applications Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => {
              const createdAt = new Date(item.created_at);
              const today = new Date();
              const diffTime = Math.abs(today - createdAt);
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              
              const statusConfig = getStatusConfig(item.status);

              return (
                <div
                  key={item.id}
                  className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow duration-300 flex flex-col"
                >
                  {/* Header with status badge */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1 min-w-0 pr-4">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {item.job.job_title}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {item.organization.company_name || "Unknown Company"}
                      </p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${statusConfig.className} flex-shrink-0`}>
                      <img src={statusConfig.icon} alt="" className="w-3 h-3" />
                      {statusConfig.text}
                    </span>
                  </div>

                  {/* Job Description */}
                  <div className="mb-4 flex-1">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {truncateText(item.job.job_description, 120)}
                    </p>
                  </div>

                  {/* Footer */}
                  <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                    <span className="text-xs text-gray-500">
                      Applied {diffDays === 0
                        ? "today"
                        : diffDays === 1
                        ? "yesterday"
                        : `${diffDays} days ago`}
                    </span>
                    
                    {/* Action button based on status */}
                    {item.status === "Interview" && (
                      <button className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                        View Details
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-span-full">
              <div className="text-center py-12">
                <div className="mb-4">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No {activeFilter.toLowerCase()} applications
                </h3>
                <p className="text-gray-500">
                  {activeFilter === "Applied" 
                    ? "Start exploring job opportunities to build your application history." 
                    : `You don't have any ${activeFilter.toLowerCase()} applications yet.`}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        {applications.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {filters.map((filter) => {
                const count = getFilterCount(filter);
                return (
                  <div key={filter} className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">{count}</div>
                    <div className="text-sm text-gray-600">{filter}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentApplications;